import axiosInstance from './axiosInstance'

// ── Types ─────────────────────────────────────────────────────────────────────

export type SearchEntityType = 'Lead' | 'Deal' | 'Contact' | 'Customer' | 'Task'
export type SearchLinkEntityType = 'Lead' | 'Deal' | 'Contact' | 'Customer'

export interface GlobalSearchResult {
  entityType:     SearchEntityType
  id:             string
  title:          string
  subtitle:       string | null
  email:          string | null
  phone:          string | null
  orgId:          string | null
  orgName:        string | null
  lobId:          string | null
  lobName:        string | null
  /** The id to deep-link to — equal to `id` except for Tasks, which link to their parent Lead/Deal. */
  linkId:         string
  /** The entity type `linkId` belongs to — use this (not `entityType`) to build the route. */
  linkEntityType: SearchLinkEntityType
  updatedAt:      string
}

// ── API ───────────────────────────────────────────────────────────────────────

export const searchApi = {
  /**
   * Global search across Leads, Deals, Contacts, Customers and Tasks.
   * Role-based scoping (Organisation/LOB/owner) is enforced entirely server-side.
   */
  search: (term: string, limitPerType = 5): Promise<GlobalSearchResult[]> =>
    axiosInstance
      .get<{ data: GlobalSearchResult[] }>('/search', { params: { q: term, limit: limitPerType } })
      .then(r => r.data.data),
}

// ── Route helper ──────────────────────────────────────────────────────────────

const ENTITY_BASE_ROUTE: Record<SearchLinkEntityType, string> = {
  Lead:     '/leads',
  Deal:     '/deals',
  Contact:  '/contacts',
  Customer: '/customers',
}

export function searchResultHref(result: GlobalSearchResult): string {
  const base = ENTITY_BASE_ROUTE[result.linkEntityType] ?? '/'
  return `${base}/${result.linkId}`
}

export const SEARCH_GROUP_LABELS: Record<SearchEntityType, string> = {
  Lead:     'Leads',
  Deal:     'Deals',
  Contact:  'Contacts',
  Customer: 'Companies',
  Task:     'Tasks',
}

export const SEARCH_GROUP_ORDER: SearchEntityType[] = ['Lead', 'Deal', 'Contact', 'Customer', 'Task']
